import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../../auth/apiFetch';

const bookcardUrl = process.env.REACT_APP_BOOKCARD_URL || process.env.REACT_APP_API_URL || window.location.origin;
const ZIP_POLL_INTERVAL_MS = 2000;

export function useBookcardSet() {
  const [prsets, setPrsets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [presetsError, setPresetsError] = useState(null);

  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const [zipLoading, setZipLoading] = useState(false);
  const [zipError, setZipError] = useState(null);
  const [zipProgress, setZipProgress] = useState(null); // { completed, total }
  const [zipDownloadUrl, setZipDownloadUrl] = useState(null);
  const zipPollRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (zipPollRef.current) clearInterval(zipPollRef.current);
    };
  }, []);

  useEffect(() => {
    apiFetch(`${bookcardUrl}/api/production-set/prsets`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setPrsets)
      .catch((e) => setPresetsError(e.message))
      .finally(() => setPresetsLoading(false));
  }, []);

  const loadProducts = async (prset) => {
    setProductsLoading(true);
    setProductsError(null);
    setProducts(null);
    try {
      const r = await apiFetch(`${bookcardUrl}/api/production-set/${encodeURIComponent(prset)}/products`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setProducts(data.products);
    } catch (e) {
      setProductsError(e.message);
    } finally {
      setProductsLoading(false);
    }
  };

  const stopZipPolling = () => {
    if (zipPollRef.current) {
      clearInterval(zipPollRef.current);
      zipPollRef.current = null;
    }
  };

  const pollZipStatus = async (jobId) => {
    try {
      const r = await apiFetch(`${bookcardUrl}/api/bookcard/zip/${jobId}/status`);
      if (r.status === 404) {
        // Stale/unknown job — e.g. the ECS task restarted mid-job (idle-shutdown or the
        // manual start/stop button can stop it at any time, independent of in-flight work).
        stopZipPolling();
        setZipError('The ZIP job expired — please try again.');
        setZipLoading(false);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setZipProgress({ completed: data.completed, total: data.total });

      if (data.status === 'DONE') {
        stopZipPolling();
        setZipLoading(false);
        // Rendered as a link for the user to click, rather than auto-navigating — downloadUrl
        // is a presigned S3 URL, not a bookcard-service API call, so no apiFetch/blob step needed.
        setZipDownloadUrl(data.downloadUrl);
      } else if (data.status === 'FAILED') {
        stopZipPolling();
        setZipLoading(false);
        setZipError(data.errorMessage ? `ZIP generation failed: ${data.errorMessage}` : 'ZIP generation failed.');
      }
    } catch (e) {
      stopZipPolling();
      setZipError(e.message);
      setZipLoading(false);
    }
  };

  const downloadZip = async (eans, label) => {
    setZipLoading(true);
    setZipError(null);
    setZipProgress(null);
    setZipDownloadUrl(null);
    try {
      const r = await apiFetch(`${bookcardUrl}/api/bookcard/zip?label=${encodeURIComponent(label)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eans),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setZipProgress({ completed: 0, total: data.total });
      zipPollRef.current = setInterval(() => pollZipStatus(data.jobId), ZIP_POLL_INTERVAL_MS);
      pollZipStatus(data.jobId);
    } catch (e) {
      setZipError(e.message);
      setZipLoading(false);
    }
  };

  return {
    prsets, presetsLoading, presetsError,
    products, productsLoading, productsError,
    loadProducts,
    zipLoading, zipError, zipProgress, zipDownloadUrl, downloadZip,
  };
}

export function currentPrset(prsets) {
  const currentYear = new Date().getFullYear();
  return prsets
    .filter((p) => {
      const m = p.match(/^PRSET(\d{4})DS(\d{2})$/);
      return m && parseInt(m[1], 10) <= currentYear;
    })
    .at(-1) ?? null;
}

export function formatPrset(prset) {
  const m = prset.match(/^PRSET(\d{4})DS(\d{2})$/);
  if (!m) return prset;
  return `${m[1]} – Set ${m[2]}`;
}

export function isBlocked(product) {
  const price = product.language === 'GER' ? product.price_de : product.price_en;
  return !price || price.trim() === '';
}
